import type { ApiErrorBody } from './types';

/* HTTP client for the admin web app.
 *
 * Design contract (bug #18):
 *   - access token lives in memory only (never written to storage).
 *   - refresh token is delivered as an HttpOnly cookie by the backend,
 *     so the client never reads it. `credentials: 'include'` is set on
 *     every call so the browser attaches the cookie automatically.
 *
 * Refresh flow:
 *   - On 401 from a non-auth endpoint, the client calls /v1/auth/refresh
 *     once, swaps in the new access token, and retries the original
 *     request. A single shared promise dedupes concurrent refreshes.
 *   - If refresh itself fails, the access token is cleared and the
 *     caller sees a 401 — protected routes will redirect to /login.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface AuthHooks {
  getAccessToken: () => string | null;
  setAccessToken: (token: string | null) => void;
  /** Called when refresh fails — store should clear and redirect. */
  onAuthInvalidated: () => void;
}

let hooks: AuthHooks | null = null;

/** Install the auth bridge from the store. Called once at app start. */
export function configureApiClient(h: AuthHooks): void {
  hooks = h;
}

const REFRESH_PATH = '/v1/auth/refresh';
const LOGIN_PATHS = [
  '/v1/auth/operator/login',
  '/v1/auth/admin/login',
  '/v1/auth/client/login',
];

let inflightRefresh: Promise<boolean> | null = null;

/** Coalesce concurrent refresh calls — at most one /refresh in-flight. */
async function refreshOnce(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    const resp = await fetch(REFRESH_PATH, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!resp.ok) return false;
    const body = (await resp.json()) as { access_token?: string };
    if (!body.access_token) return false;
    hooks?.setAccessToken(body.access_token);
    return true;
  })();
  try {
    return await inflightRefresh;
  } finally {
    inflightRefresh = null;
  }
}

/** Force a single token refresh (coalesced). Used by the SSE client
    on a 401 from the stream endpoint, which can't go through
    `apiRequest`. Returns true if a fresh access token is now set. */
export async function forceRefresh(): Promise<boolean> {
  return refreshOnce();
}

interface RequestInit_ extends Omit<RequestInit, 'body'> {
  json?: unknown;
  /** Multipart body (chat media upload). Content-type is left unset so the
      browser supplies the multipart boundary. */
  form?: FormData;
}

async function rawFetch(
  path: string,
  init: RequestInit_ = {},
  token: string | null,
): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  if (init.json !== undefined) headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);
  const body =
    init.json !== undefined
      ? JSON.stringify(init.json)
      : init.form !== undefined
        ? init.form
        : undefined;
  return fetch(path, { ...init, headers, credentials: 'include', body });
}

/** Auth + single-refresh-retry around a request, returning the raw OK
    response. Throws an ApiError on any non-2xx (after the retry). Shared by
    the JSON and blob entry points. */
async function requestWithRetry(path: string, init: RequestInit_): Promise<Response> {
  const token = hooks?.getAccessToken() ?? null;
  let resp = await rawFetch(path, init, token);

  // Don't try to refresh /login or /refresh — they're already auth boundaries.
  const isAuthBoundary = path === REFRESH_PATH || LOGIN_PATHS.some((p) => path === p);
  if (resp.status === 401 && !isAuthBoundary) {
    const ok = await refreshOnce();
    if (!ok) {
      hooks?.setAccessToken(null);
      hooks?.onAuthInvalidated();
      throw await toApiError(resp);
    }
    const next = hooks?.getAccessToken() ?? null;
    resp = await rawFetch(path, init, next);
  }

  if (!resp.ok) throw await toApiError(resp);
  return resp;
}

export async function apiRequest<T>(path: string, init: RequestInit_ = {}): Promise<T> {
  const resp = await requestWithRetry(path, init);
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

/** Fetch binary content (chat attachment) with the same auth/refresh flow.
    Callers wrap the Blob in an object URL for <img>/<audio> rendering — the
    download endpoint needs a Bearer token, so a plain element `src` can't
    reach it. */
export async function apiBlob(path: string): Promise<Blob> {
  const resp = await requestWithRetry(path, {});
  return resp.blob();
}

async function toApiError(resp: Response): Promise<ApiError> {
  let code: string | undefined;
  let message = resp.statusText;
  try {
    const body = (await resp.json()) as ApiErrorBody;
    if (body?.error) {
      code = body.error.code;
      message = body.error.message;
    }
  } catch {
    // not JSON — keep statusText
  }
  return new ApiError(message, resp.status, code);
}
